import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Divider,
    List,
    ListItem,
    Chip,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    LinearProgress,
    Collapse,
    IconButton,
} from '@mui/material';
import {
    ArrowLeft,
    Shield,
    Zap,
    Hand,
    CheckCircle,
    ChevronDown,
} from 'lucide-react';
import { getData, postData } from '../api/axiosConfig';
import { useAuth } from '../context/AuthContext';
export const ComplianceReport = () => {
    const { taskId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const userEmail = user?.email || 'anonymous@user.local';
    const [loading, setLoading] = useState(true);
    const [report, setReport] = useState(null);
    const [error, setError] = useState(null);
    const [remediating, setRemediating] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pollingStatus, setPollingStatus] = useState(null);

    const fetchReport = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getData(`/api/documents/${taskId}/compliance-report?user_id=${encodeURIComponent(userEmail)}`);
            setReport(data);

            // If already remediated, redirect to detail page
            if (data.status === 'remediated') {
                navigate(`/files/${taskId}`, { replace: true });
                return;
            }
        } catch (err) {
            console.error("Error fetching compliance report:", err);
            setError("Failed to load compliance report.");
        } finally {
            setLoading(false);
        }
    }, [taskId, navigate]);

    useEffect(() => {
        if (taskId) fetchReport();
    }, [taskId, fetchReport]);

    // Poll for status while remediating
    useEffect(() => {
        if (!remediating) return;

        const interval = setInterval(async () => {
            try {
                const data = await getData(`/api/documents/${taskId}?user_id=${encodeURIComponent(userEmail)}`);
                const status = data?.document?.status;
                setPollingStatus(status);

                if (status === 'remediated') {
                    setRemediating(false);
                    clearInterval(interval);
                    navigate(`/files/${taskId}`, { replace: true });
                } else if (status === 'failed') {
                    setRemediating(false);
                    clearInterval(interval);
                    setError('Remediation failed. Please try again.');
                }
            } catch (e) {
                console.error("Polling error:", e);
            }
        }, 3000);

        return () => clearInterval(interval);
    }, [remediating, taskId, navigate]);

    const handleRemediate = async () => {
        setConfirmOpen(false);
        setRemediating(true);
        try {
            await postData(`/api/documents/${taskId}/remediate?user_id=${encodeURIComponent(userEmail)}`, {});
        } catch (err) {
            console.error("Remediation trigger error:", err);
            setRemediating(false);
            setError("Failed to start remediation.");
        }
    };

    const handleBack = () => navigate('/files');

    const { summary, auto_fixable_issues = [], human_review_issues = [] } = report || {};

    // Group human review issues by title (must be before early returns for hook ordering)
    const groupedHumanReviewIssues = useMemo(() => {
        const groups = {};
        human_review_issues.forEach((issue) => {
            const key = issue.title || 'Unknown Issue';
            if (!groups[key]) {
                groups[key] = {
                    title: key,
                    severity: issue.severity,
                    wcag_criterion: issue.wcag_criterion,
                    instances: [],
                };
            }
            groups[key].instances.push(issue);
        });
        return Object.values(groups);
    }, [human_review_issues]);

    const [expandedGroups, setExpandedGroups] = useState({});
    const toggleGroup = (title) => {
        setExpandedGroups((prev) => ({ ...prev, [title]: !prev[title] }));
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Button startIcon={<ArrowLeft />} onClick={handleBack} sx={{ mb: 2 }}>
                    Back to Files
                </Button>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h5" color="error" gutterBottom>Error</Typography>
                    <Typography>{error}</Typography>
                </Paper>
            </Container>
        );
    }

    const getSeverityColor = (severity) => {
        const s = (severity || '').toLowerCase();
        if (s === 'critical') return '#dc2626';
        if (s === 'major') return '#f59e0b';
        return '#6b7280';
    };

    const getSeverityChipColor = (severity) => {
        const s = (severity || '').toLowerCase();
        if (s === 'critical') return 'error';
        if (s === 'major') return 'warning';
        return 'default';
    };

    const IssueRow = ({ issue }) => (
        <div>
            <ListItem alignItems="flex-start" sx={{ px: 3, py: 1.5 }}>
                <Box sx={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'hsl(var(--foreground))' }}>
                                {issue.title}
                            </Typography>
                            <Chip
                                label={issue.severity}
                                size="small"
                                variant="outlined"
                                color={getSeverityChipColor(issue.severity)}
                                sx={{ height: '20px', fontSize: '0.7rem' }}
                            />
                            {issue.wcag_criterion && (
                                <Chip
                                    label={`WCAG ${issue.wcag_criterion}`}
                                    size="small"
                                    sx={{ height: '20px', fontSize: '0.7rem', backgroundColor: '#f3f4f6' }}
                                />
                            )}
                        </div>
                        <Typography variant="caption" color="text.secondary">
                            Page {issue.page_num}
                        </Typography>
                    </div>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                        {issue.description}
                    </Typography>
                </Box>
            </ListItem>
            <Divider component="li" />
        </div>
    );

    const GroupedIssueRow = ({ group }) => {
        const isExpanded = expandedGroups[group.title] || false;
        const count = group.instances.length;
        return (
            <div>
                <ListItem
                    alignItems="flex-start"
                    sx={{
                        px: 3, py: 1.5,
                        cursor: 'pointer',
                        transition: 'background-color 0.15s',
                        '&:hover': { backgroundColor: 'hsl(var(--warning) / 0.04)' },
                    }}
                    onClick={() => toggleGroup(group.title)}
                >
                    <Box sx={{ width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                <IconButton
                                    size="small"
                                    sx={{
                                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s',
                                        p: 0.3,
                                    }}
                                >
                                    <ChevronDown size={16} />
                                </IconButton>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'hsl(var(--foreground))' }}>
                                    {group.title}
                                </Typography>
                                <Chip
                                    label={group.severity}
                                    size="small"
                                    variant="outlined"
                                    color={getSeverityChipColor(group.severity)}
                                    sx={{ height: '20px', fontSize: '0.7rem' }}
                                />
                                {group.wcag_criterion && (
                                    <Chip
                                        label={`WCAG ${group.wcag_criterion}`}
                                        size="small"
                                        sx={{ height: '20px', fontSize: '0.7rem', backgroundColor: '#f3f4f6' }}
                                    />
                                )}
                                {count > 1 && (
                                    <Chip
                                        label={`×${count} instances`}
                                        size="small"
                                        sx={{
                                            height: '22px',
                                            fontSize: '0.72rem',
                                            fontWeight: 700,
                                            backgroundColor: 'hsl(var(--warning) / 0.12)',
                                            color: 'hsl(var(--warning))',
                                            border: '1px solid hsl(var(--warning) / 0.25)',
                                        }}
                                    />
                                )}
                            </div>
                            <Typography variant="caption" color="text.secondary">
                                {count === 1 ? `Page ${group.instances[0].page_num}` : `${count} pages`}
                            </Typography>
                        </div>
                        {!isExpanded && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', pl: 3.5 }}>
                                {group.instances[0].description}
                            </Typography>
                        )}
                    </Box>
                </ListItem>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ backgroundColor: 'hsl(var(--muted) / 0.3)', borderTop: '1px solid hsl(var(--border) / 0.5)' }}>
                        {group.instances.map((instance, idx) => (
                            <Box
                                key={instance.finding_id || idx}
                                sx={{
                                    px: 3, pl: 6, py: 1.2,
                                    borderBottom: idx < count - 1 ? '1px solid hsl(var(--border) / 0.3)' : 'none',
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'hsl(var(--foreground) / 0.7)' }}>
                                        Instance {idx + 1}
                                    </Typography>
                                    <Chip
                                        label={`Page ${instance.page_num}`}
                                        size="small"
                                        sx={{ height: '18px', fontSize: '0.65rem', backgroundColor: 'hsl(var(--accent))' }}
                                    />
                                </div>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                    {instance.description}
                                </Typography>
                                {instance.element_text && (
                                    <Typography variant="caption" sx={{
                                        display: 'block', mt: 0.5,
                                        color: 'hsl(var(--muted-foreground))',
                                        fontStyle: 'italic', fontSize: '0.75rem',
                                    }}>
                                        "{instance.element_text}"
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Collapse>
                <Divider component="li" />
            </div>
        );
    };

    return (
        <div className="space-y-6 animate-fade-in" style={{ paddingBottom: '40px' }}>
            {/* Remediating Overlay */}
            {remediating && (
                <Dialog open={true} PaperProps={{ sx: { borderRadius: '16px', p: 2, minWidth: 400 } }}>
                    <DialogContent sx={{ textAlign: 'center', py: 4 }}>
                        <Box sx={{ mb: 3 }}>
                            <CircularProgress size={48} sx={{ color: 'hsl(var(--primary))' }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'hsl(var(--foreground))' }}>
                            Remediating Document
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            AI is fixing accessibility issues... This may take a moment.
                        </Typography>
                        <LinearProgress
                            sx={{
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: '#e5e7eb',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 3,
                                    background: 'var(--gradient-primary)',
                                },
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                            Status: {pollingStatus || 'remediating'}...
                        </Typography>
                    </DialogContent>
                </Dialog>
            )}

            {/* Header */}
            <div className="mb-6">
                <Button
                    startIcon={<ArrowLeft size={18} />}
                    onClick={handleBack}
                    sx={{
                        mb: 2,
                        color: 'hsl(var(--muted-foreground))',
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        '&:hover': { backgroundColor: 'hsl(var(--accent))', color: 'hsl(var(--foreground))' }
                    }}
                >
                    Back to Files
                </Button>

                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="service-icon" style={{ width: '48px', height: '48px' }}>
                            <Shield size={24} />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold tracking-tight text-foreground">Compliance Report</h2>
                            <p className="text-sm text-muted-foreground">{report?.filename || 'Document'}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Score & Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                    <Card
                        className="ai-card animate-slide-up"
                        elevation={0}
                        sx={{
                            height: '100%',
                            background: 'var(--gradient-background)',
                            border: '1px solid hsl(var(--border))',
                            animationDelay: '0.1s'
                        }}
                    >
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                                Compliance Score
                            </Typography>
                            <Typography variant="h2" sx={{
                                fontWeight: 800,
                                color: (report?.initial_compliance_score || 0) >= 80 ? 'hsl(var(--success))' :
                                    (report?.initial_compliance_score || 0) >= 50 ? 'hsl(var(--warning))' : 'hsl(var(--destructive))',
                                lineHeight: 1.2,
                            }}>
                                {report?.initial_compliance_score || 0}%
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card className="ai-card animate-slide-up" elevation={0} sx={{ height: '100%', animationDelay: '0.2s' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                                Total Issues
                            </Typography>
                            <Typography variant="h3" sx={{ fontWeight: 700, color: 'hsl(var(--foreground))' }}>
                                {(summary?.auto_fixable_count || 0) + groupedHumanReviewIssues.length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card className="ai-card animate-slide-up" elevation={0} sx={{
                        height: '100%', animationDelay: '0.3s',
                        border: '1px solid hsl(var(--success) / 0.3)', background: 'hsl(var(--success) / 0.02)',
                    }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                <Zap size={16} color="hsl(var(--success))" />
                                <Typography variant="overline" sx={{ fontWeight: 600, color: 'hsl(var(--success))' }}>
                                    Auto-Fixable
                                </Typography>
                            </div>
                            <Typography variant="h3" sx={{ fontWeight: 700, color: 'hsl(var(--success))' }}>
                                {summary?.auto_fixable_count || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card className="ai-card animate-slide-up" elevation={0} sx={{
                        height: '100%', animationDelay: '0.4s',
                        border: '1px solid hsl(var(--warning) / 0.3)', background: 'hsl(var(--warning) / 0.02)',
                    }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                <Hand size={16} color="hsl(var(--warning))" />
                                <Typography variant="overline" sx={{ fontWeight: 600, color: 'hsl(var(--warning))' }}>
                                    Human Review
                                </Typography>
                            </div>
                            <Typography variant="h3" sx={{ fontWeight: 700, color: 'hsl(var(--warning))' }}>
                                {groupedHumanReviewIssues.length || summary?.human_review_count || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Remediate CTA */}
            {report?.status === 'completed' && (summary?.auto_fixable_count || 0) > 0 && (
                <Paper
                    elevation={0}
                    className="ai-card animate-slide-up"
                    sx={{
                        animationDelay: '0.5s',
                        border: '1px solid hsl(var(--primary) / 0.2)',
                        background: 'hsl(var(--primary) / 0.03)',
                        p: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        hoverScale: 'none',
                    }}
                >
                    <div>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'hsl(var(--primary))' }}>
                            Ready to Remediate
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'hsl(var(--foreground))', opacity: 0.8 }}>
                            AI can automatically fix {summary?.auto_fixable_count} of {(summary?.auto_fixable_count || 0) + groupedHumanReviewIssues.length} issues.
                            {groupedHumanReviewIssues.length > 0 && ` ${groupedHumanReviewIssues.length} issues will still require human review.`}
                        </Typography>
                    </div>
                    <Button
                        variant="contained"
                        className="btn-gradient"
                        size="large"
                        onClick={() => setConfirmOpen(true)}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            px: 4,
                            py: 1.2,
                            borderRadius: 'var(--radius)',
                        }}
                    >
                        Remediate Now
                    </Button>
                </Paper>
            )}

            {/* Confirmation Dialog */}
            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    Confirm Remediation
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will run AI-powered remediation on <strong>{report?.filename}</strong> to
                        automatically fix <strong>{summary?.auto_fixable_count}</strong> accessibility issues.
                        The process may take a few minutes.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setConfirmOpen(false)}
                        sx={{ textTransform: 'none', color: '#6b7280' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleRemediate}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '10px',
                            background: 'var(--gradient-primary)',
                            '&:hover': { background: 'var(--gradient-primary)', opacity: 0.9 },
                        }}
                    >
                        Start Remediation
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Auto-Fixable Issues Section */}
            {auto_fixable_issues.length > 0 && (
                <Paper
                    elevation={0}
                    className="ai-card animate-slide-up"
                    sx={{
                        animationDelay: '0.6s',
                        border: '1px solid hsl(var(--success) / 0.2)',
                        overflow: 'hidden',
                        p: 0,
                    }}
                >
                    <Box sx={{
                        px: 3, py: 2,
                        backgroundColor: 'hsl(var(--success) / 0.05)',
                        borderBottom: '1px solid hsl(var(--success) / 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                    }}>
                        <Zap size={20} color="hsl(var(--success))" />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'hsl(var(--success))' }}>
                            Auto-Fixable by AI ({auto_fixable_issues.length})
                        </Typography>
                        <Chip
                            label="Will be fixed automatically"
                            size="small"
                            sx={{ backgroundColor: 'hsl(var(--success) / 0.1)', color: 'hsl(var(--success))', fontWeight: 600, fontSize: '0.7rem' }}
                        />
                    </Box>
                    <List disablePadding>
                        {auto_fixable_issues.map((issue, idx) => (
                            <IssueRow key={issue.finding_id || idx} issue={issue} />
                        ))}
                    </List>
                </Paper>
            )}

            {/* Human Review Issues Section */}
            {human_review_issues.length > 0 && (
                <Paper
                    elevation={0}
                    className="ai-card animate-slide-up"
                    sx={{
                        animationDelay: '0.7s',
                        border: '1px solid hsl(var(--warning) / 0.2)',
                        overflow: 'hidden',
                        p: 0,
                    }}
                >
                    <Box sx={{
                        px: 3, py: 2,
                        backgroundColor: 'hsl(var(--warning) / 0.05)',
                        borderBottom: '1px solid hsl(var(--warning) / 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                    }}>
                        <Hand size={20} color="hsl(var(--warning))" />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'hsl(var(--warning))' }}>
                            Requires Human Review ({groupedHumanReviewIssues.length}{groupedHumanReviewIssues.length !== human_review_issues.length ? ` issues` : ''})
                        </Typography>
                        {groupedHumanReviewIssues.length !== human_review_issues.length && (
                            <Chip
                                label={`${human_review_issues.length} total instances`}
                                size="small"
                                sx={{ backgroundColor: 'hsl(var(--warning) / 0.1)', color: 'hsl(var(--warning))', fontWeight: 600, fontSize: '0.7rem' }}
                            />
                        )}
                        <Chip
                            label="Manual intervention needed"
                            size="small"
                            sx={{ backgroundColor: 'hsl(var(--warning) / 0.1)', color: 'hsl(var(--warning))', fontWeight: 600, fontSize: '0.7rem' }}
                        />
                    </Box>
                    <List disablePadding>
                        {groupedHumanReviewIssues.map((group, idx) => (
                            <GroupedIssueRow key={group.title || idx} group={group} />
                        ))}
                    </List>
                </Paper>
            )}

            {/* No issues fallback */}
            {(auto_fixable_issues.length === 0 && human_review_issues.length === 0) && (
                <Paper
                    elevation={0}
                    className="ai-card animate-scale-in"
                    sx={{
                        border: '1px solid hsl(var(--success) / 0.2)',
                        p: 6,
                        textAlign: 'center',
                    }}
                >
                    <CheckCircle size={48} color="#10b981" />
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 2, color: '#065f46' }}>
                        No Issues Found!
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                        This document appears to be fully compliant.
                    </Typography>
                </Paper>
            )}
        </div>
    );
};
